import { Injectable } from '@angular/core';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SelfV2 } from 'src/app/core/alcmeon-context/models/self-v2.model';
import { SiteV2 } from 'src/app/shared/models';
import { TaskStateInWsNotif } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { StrategiesService } from 'src/app/shared/strategies/strategies.service';

import { QueueIdEnum } from '../shared/enums';
import { TaskListConfiguration } from '../shared/models';

@Injectable({
  providedIn: 'root',
})
export class NavigationConfigurationService {
  constructor(
    private basicService: BasicService,
    private strategiesService: StrategiesService,
    private alcmeonContextService: AlcmeonContextService
  ) {}

  /**
   * Get the task list config for the tabs and sub tabs navigation.
   */
  getTabsConfiguration(
    siteIds: number[],
    routingTagsIds: number[],
    selectedSiteGroupIds: number[],
    loggedUser: SelfV2
  ): TaskListConfiguration[] {
    return [
      // 1st tab "received" (on all channels), no sub tab
      // (same configuration as one site (old AJS config), without parameters.type)
      {
        name: this.basicService.translate('inbox/tab/received'),
        title: this.basicService.translate('inbox/tab/received'),
        queueId: QueueIdEnum.Received,
        isSelected: false,
        parameters: {
          sites_ids: siteIds,
          site_group_ids: selectedSiteGroupIds,
          states: [TaskStateInWsNotif.Pending, TaskStateInWsNotif.HelpAnswered],
          routingTagsIds: routingTagsIds,
          // TasksIds.py doesn't filter with the 'admin_users_ids' or 'exclude_admin_users_ids' parameters
          // for the sub-query on pending tasks
          exclude_admin_users_ids: [loggedUser.id],
        },
      },

      // 2nd tab "ongoing"
      {
        name: this.basicService.translate('inbox/tab/ongoing'),
        counterIndex: -1, // calculate counters in component
        children: [
          // sub tab "by me" all in one : assigned, on hold, help answered
          {
            name: `${this.basicService.translate('inbox/tab/ongoing')} ${this.basicService.translate(
              'inbox/tab/by-me'
            )}`,
            title: `${this.basicService.translate('inbox/tab/ongoing')} ${this.basicService.translate(
              'inbox/tab/by-me'
            )}`,
            queueId: QueueIdEnum.AssignedToYou,
            isSelected: true,
            parameters: {
              sites_ids: siteIds,
              site_group_ids: selectedSiteGroupIds,
              states: [TaskStateInWsNotif.Assigned, TaskStateInWsNotif.OnHold, TaskStateInWsNotif.HelpAnswered],
              routingTagsIds: routingTagsIds,
              admin_users_ids: [loggedUser.id],
            },
          },

          // sub tab assigned (by others)
          {
            name: this.basicService.translate('inbox/tab/assigned'),
            title: `${this.basicService.translate('inbox/tab/ongoing')} - ${this.basicService.translate(
              'inbox/tab/assigned'
            )}`,
            queueId: QueueIdEnum.AssignedToOthers,
            isSelected: false,
            parameters: {
              sites_ids: siteIds,
              site_group_ids: selectedSiteGroupIds,
              states: [TaskStateInWsNotif.Assigned],
              routingTagsIds: routingTagsIds,
              exclude_admin_users_ids: [loggedUser.id],
            },
          },

          // sub tab on hold (by others)
          {
            name: this.basicService.translate('inbox/tab/on-hold'),
            title: `${this.basicService.translate('inbox/tab/ongoing')} - ${this.basicService.translate(
              'inbox/tab/on-hold'
            )}`,
            queueId: QueueIdEnum.OnHoldByOthers,
            isSelected: false,
            parameters: {
              sites_ids: siteIds,
              site_group_ids: selectedSiteGroupIds,
              states: [TaskStateInWsNotif.OnHold],
              routingTagsIds: routingTagsIds,
              exclude_admin_users_ids: [loggedUser.id],
            },
          },

          // sub tab help requested (by everyone)
          {
            name: this.basicService.translate('inbox/tab/help-requests'),
            title: `${this.basicService.translate('inbox/tab/ongoing')} - ${this.basicService.translate(
              'inbox/tab/help-requests'
            )}`,
            queueId: QueueIdEnum.HelpRequested,
            isSelected: false,
            parameters: {
              sites_ids: siteIds,
              site_group_ids: selectedSiteGroupIds,
              states: [TaskStateInWsNotif.HelpRequested],
              routingTagsIds: routingTagsIds,
            },
          },
        ],
      },

      // 3rd tab "answered"
      {
        name: this.basicService.translate('inbox/tab/answered'),
        children: [
          // sub tab "by me"
          {
            name: this.basicService.translate('inbox/tab/by-me'),
            title: `${this.basicService.translate('inbox/tab/answered')} ${this.basicService.translate(
              'inbox/tab/by-me'
            )}`,
            queueId: QueueIdEnum.AnsweredByMe,
            isSelected: true,
            parameters: {
              sites_ids: siteIds,
              site_group_ids: selectedSiteGroupIds,
              states: [TaskStateInWsNotif.Answered],
              routingTagsIds: routingTagsIds,
              admin_users_ids: [loggedUser.id],
            },
          },
          // sub tab "by others"
          {
            name: this.basicService.translate('inbox/tab/by-others'),
            title: `${this.basicService.translate('inbox/tab/answered')} ${this.basicService.translate(
              'inbox/tab/by-others'
            )}`,
            queueId: QueueIdEnum.AnsweredByOthers,
            isSelected: false,
            parameters: {
              sites_ids: siteIds,
              site_group_ids: selectedSiteGroupIds,
              states: [TaskStateInWsNotif.Answered],
              routingTagsIds: routingTagsIds,
              exclude_admin_users_ids: [loggedUser.id],
            },
          },
        ],
      },

      // 4th tab "archived"
      {
        name: this.basicService.translate('inbox/tab/archived'),
        children: [
          // sub tab "by me"
          {
            name: this.basicService.translate('inbox/tab/by-me'),
            title: `${this.basicService.translate('inbox/tab/archived')} ${this.basicService.translate(
              'inbox/tab/by-me'
            )}`,
            queueId: QueueIdEnum.ArchivedByMe,
            isSelected: true,
            parameters: {
              sites_ids: siteIds,
              site_group_ids: selectedSiteGroupIds,
              states: [TaskStateInWsNotif.Archived],
              routingTagsIds: routingTagsIds,
              admin_users_ids: [loggedUser.id],
            },
          },
          // sub tab "by others"
          {
            name: this.basicService.translate('inbox/tab/by-others'),
            title: `${this.basicService.translate('inbox/tab/archived')} ${this.basicService.translate(
              'inbox/tab/by-others'
            )}`,
            queueId: QueueIdEnum.ArchivedByOthers,
            isSelected: false,
            parameters: {
              sites_ids: siteIds,
              site_group_ids: selectedSiteGroupIds,
              states: [TaskStateInWsNotif.Archived],
              routingTagsIds: routingTagsIds,
              exclude_admin_users_ids: [loggedUser.id],
            },
          },
        ],
      },
    ];
  }

  getChannelsConfiguration(sitesForConfigs: SiteV2[]): TaskListConfiguration[] {
    const channelsTaskListConfigs: TaskListConfiguration[] = [];

    // first item "All channels"
    channelsTaskListConfigs.push({
      name: this.basicService.translate('inbox/channels/all'),
      title: this.basicService.translate('inbox/channels/all'),
      queueId: QueueIdEnum.All,
      isSelected: false,
      typeString: 'all',
      parameters: {
        type: null,
        wantsPublicationFilter: false,
        wantsMentionFilter: false,
        wantsPublicRepliesFilter: false,
      },
    });

    const stategiesToDisplay = this.strategiesService
      .getAll()
      .filter(strategy => sitesForConfigs.some(site => site.type === strategy.getType()));

    // list of channels
    stategiesToDisplay.forEach(strategy => {
      const tasks = strategy.getAllTasksTypesForInbox();
      this.alcmeonContextService.addPublicRepliesTaskIfAllowed(strategy, tasks);
      tasks.forEach(taskType => {
        channelsTaskListConfigs.push({
          name: this.basicService.translate(`inbox/channels/${strategy.getTaskTypeString(taskType)}`),
          title: this.basicService.translate(`inbox/channels/${strategy.getTaskTypeString(taskType)}`),
          queueId: strategy.getTaskTypeQueueId(taskType),
          isSelected: false,
          typeString: strategy.getTypeString(),
          parameters: {
            type: taskType,
            wantsPublicationFilter: strategy.taskTypeHasPublicationFilter(taskType),
            wantsMentionFilter: strategy.taskTypeHasMentionFilter(taskType),
            wantsPublicRepliesFilter: strategy.taskTypeHasPublicRepliesFilter(taskType),
          },
        });
      });
    });

    return channelsTaskListConfigs;
  }

  /**
   * These counters are for colored inserts "by me" in received tab.
   * There are no tasklists behind, it's just links to the "ongoing by me" tab.
   */
  getByMeConfiguration(
    siteIdsForConfigs: number[],
    routingTagsIds: number[],
    selectedSiteGroupIds: number[],
    loggedUser: SelfV2
  ): TaskListConfiguration[] {
    return [
      //  assigned to me
      {
        parameters: {
          sites_ids: siteIdsForConfigs,
          site_group_ids: selectedSiteGroupIds,
          states: [TaskStateInWsNotif.Assigned],
          routingTagsIds: routingTagsIds,
          admin_users_ids: [loggedUser.id],
        },
      },
      // on hold by me
      {
        parameters: {
          sites_ids: siteIdsForConfigs,
          site_group_ids: selectedSiteGroupIds,
          states: [TaskStateInWsNotif.OnHold],
          routingTagsIds: routingTagsIds,
          admin_users_ids: [loggedUser.id],
        },
      },
      // help requested by me
      {
        parameters: {
          sites_ids: siteIdsForConfigs,
          site_group_ids: selectedSiteGroupIds,
          states: [TaskStateInWsNotif.HelpRequested],
          routingTagsIds: routingTagsIds,
          admin_users_ids: [loggedUser.id],
        },
      },
    ];
  }
}
